import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getWorkspaces, type Workspace } from '../api/workspaces';
import { useAuth } from './AuthProvider';

const ACTIVE_WORKSPACE_KEY = 'loopora.activeWorkspaceId';

type WorkspaceContextValue = {
  workspaces: Workspace[];
  activeWorkspace: Workspace | null;
  activeWorkspaceId: string;
  isLoading: boolean;
  selectWorkspace: (workspaceId: string) => void;
  refreshWorkspaces: () => Promise<void>;
};

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState(() => typeof window === 'undefined' ? '' : localStorage.getItem(ACTIVE_WORKSPACE_KEY) ?? '');
  const query = useQuery({ queryKey: ['workspaces'], queryFn: getWorkspaces, enabled: isAuthenticated, staleTime: 30_000 });
  const workspaces = query.data ?? [];
  const activeWorkspace = workspaces.find((item) => item.id === selectedId) ?? workspaces[0] ?? null;

  useEffect(() => {
    const nextId = activeWorkspace?.id ?? '';
    if (nextId && nextId !== selectedId) setSelectedId(nextId);
    if (nextId) localStorage.setItem(ACTIVE_WORKSPACE_KEY, nextId);
    else localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
  }, [activeWorkspace?.id, selectedId]);

  useEffect(() => {
    if (!isAuthenticated) setSelectedId('');
  }, [isAuthenticated]);

  const selectWorkspace = useCallback((workspaceId: string) => {
    if (!workspaces.some((item) => item.id === workspaceId)) return;
    setSelectedId(workspaceId);
    localStorage.setItem(ACTIVE_WORKSPACE_KEY, workspaceId);
    void queryClient.invalidateQueries({ predicate: (queryValue) => queryValue.queryKey[0] === 'workspace-data' || queryValue.queryKey[0] === 'workspace-summary' });
  }, [queryClient, workspaces]);

  const refreshWorkspaces = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['workspaces'] });
  }, [queryClient]);

  const value = useMemo<WorkspaceContextValue>(() => ({
    workspaces, activeWorkspace, activeWorkspaceId: activeWorkspace?.id ?? '', isLoading: query.isLoading, selectWorkspace, refreshWorkspaces,
  }), [activeWorkspace, query.isLoading, refreshWorkspaces, selectWorkspace, workspaces]);
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace() {
  const value = useContext(WorkspaceContext);
  if (!value) throw new Error('useWorkspace must be used within WorkspaceProvider');
  return value;
}
